import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AssignmentFormModal } from './AssignmentFormModal'
import {
  useAssignmentByLesson,
  useCreateAssignment,
  useUpdateAssignment,
  useDeleteAssignment,
  usePublishAssignment,
  useSubmissions,
} from '@/hooks'
import { useAuth } from '@/hooks/useAuth'
import type { CreateAssignmentRequest } from '@/types/assignment'

interface AssignmentManagerProps {
  lessonId: string
  lessonTitle: string
}

export const AssignmentManager: React.FC<AssignmentManagerProps> = ({ lessonId, lessonTitle }) => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data: assignment, isLoading } = useAssignmentByLesson(lessonId)
  const { data: submissions = [] } = useSubmissions(assignment?.id || '')

  const createAssignment = useCreateAssignment()
  const updateAssignment = useUpdateAssignment()
  const deleteAssignment = useDeleteAssignment()
  const publishAssignment = usePublishAssignment()

  const [showModal, setShowModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const pendingCount = submissions.filter(s => s.status === 'submitted').length

  // Determine submissions route based on user role
  const getSubmissionsRoute = () => {
    if (user?.role === 'admin') {
      return `/admin/assignments/${assignment?.id}/submissions`
    }
    return `/instructor/assignments/${assignment?.id}/submissions`
  }

  const handleCreate = async (data: CreateAssignmentRequest) => {
    await createAssignment.mutateAsync(data)
    setShowModal(false)
  }

  const handleUpdate = async (data: CreateAssignmentRequest) => {
    if (!assignment) return
    await updateAssignment.mutateAsync({ id: assignment.id, data })
    setShowModal(false)
  }

  const handleDelete = async () => {
    if (!assignment) return
    if (!confirm('Are you sure you want to delete this assignment?')) return
    setIsDeleting(true)
    try {
      await deleteAssignment.mutateAsync(assignment.id)
    } finally {
      setIsDeleting(false)
    }
  }

  const handlePublish = async () => {
    if (!assignment) return
    await publishAssignment.mutateAsync(assignment.id)
  }

  if (isLoading) {
    return (
      <div className="p-6 rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-100 dark:bg-white/10 rounded w-1/3 mb-4" />
          <div className="h-4 bg-gray-100 dark:bg-white/10 rounded w-2/3" />
        </div>
      </div>
    )
  }

  // No assignment exists yet
  if (!assignment) {
    return (
      <div className="p-6 rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10">
        <div className="text-center py-8">
          <div className="size-16 rounded-full bg-gray-100 dark:bg-white/5 mx-auto mb-4 flex items-center justify-center">
            <span className="material-symbols-outlined text-[32px] text-gray-500">assignment</span>
          </div>
          <h4 className="text-gray-900 dark:text-white font-bold mb-2">No Assignment Created</h4>
          <p className="text-sm text-gray-500 dark:text-text-muted mb-4">
            Create an assignment for "{lessonTitle}"
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg font-bold hover:bg-primary/90 transition-colors"
          >
            <span className="material-symbols-outlined text-lg">add</span>
            Create Assignment
          </button>
        </div>

        {showModal && (
          <AssignmentFormModal
            lessonId={lessonId}
            onSave={handleCreate}
            onClose={() => setShowModal(false)}
            isSaving={createAssignment.isPending}
          />
        )}
      </div>
    )
  }

  // Assignment exists
  return (
    <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 overflow-hidden">
      {/* Assignment Header */}
      <div className="p-5 border-b border-gray-200 dark:border-white/10">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="text-lg font-bold text-gray-900 dark:text-white">{assignment.title}</h4>
              <span
                className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                  assignment.isPublished
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-yellow-500/20 text-yellow-400'
                }`}
              >
                {assignment.isPublished ? 'Published' : 'Draft'}
              </span>
            </div>
            {assignment.description && (
              <p className="text-sm text-gray-500 dark:text-text-muted mb-3">{assignment.description}</p>
            )}
            <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 dark:text-text-muted">
              <span className="flex items-center gap-1">
                <span className="material-symbols-outlined text-sm">grade</span>
                Max: {assignment.maxScore} pts
              </span>
              <span className="flex items-center gap-1">
                <span className="material-symbols-outlined text-sm">percent</span>
                Pass: {assignment.passingScore}%
              </span>
              {assignment.dueDateDays && (
                <span className="flex items-center gap-1">
                  <span className="material-symbols-outlined text-sm">calendar_today</span>
                  Due in {assignment.dueDateDays} days
                </span>
              )}
              <span className="flex items-center gap-1">
                <span className="material-symbols-outlined text-sm">upload_file</span>
                Max {assignment.maxFileSizeMB} MB
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!assignment.isPublished && (
              <button
                onClick={handlePublish}
                disabled={publishAssignment.isPending}
                className="px-3 py-2 rounded-lg bg-green-500/20 text-green-400 text-sm font-medium hover:bg-green-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                title="Publish assignment"
              >
                <span className="material-symbols-outlined text-base">publish</span>
                Publish
              </button>
            )}
            <button
              onClick={() => setShowModal(true)}
              className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
              title="Edit assignment"
            >
              <span className="material-symbols-outlined text-gray-500 dark:text-gray-400">edit</span>
            </button>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="p-2 rounded-lg hover:bg-red-500/20 transition-colors"
              title="Delete assignment"
            >
              <span className="material-symbols-outlined text-red-400">delete</span>
            </button>
          </div>
        </div>
      </div>

      {/* View Submissions Button */}
      <div className="p-5 border-b border-gray-200 dark:border-white/10">
        <button
          onClick={() => navigate(getSubmissionsRoute())}
          className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-primary/10 hover:bg-primary/20 border border-primary/30 transition-colors group"
        >
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-lg bg-primary/20 flex items-center justify-center group-hover:bg-primary/30 transition-colors">
              <span className="material-symbols-outlined text-primary">grading</span>
            </div>
            <div className="text-left">
              <div className="text-sm font-bold text-gray-900 dark:text-white">View Submissions</div>
              <div className="text-xs text-gray-500 dark:text-text-muted">
                {submissions.length === 0 ? 'No submissions yet' : `${submissions.length} total submission${submissions.length > 1 ? 's' : ''}`}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {pendingCount > 0 && (
              <div className="px-3 py-1.5 rounded-full bg-orange-500/20 text-orange-400 text-xs font-bold">
                {pendingCount} pending
              </div>
            )}
            <span className="material-symbols-outlined text-gray-500 dark:text-gray-400 group-hover:text-primary transition-colors">
              arrow_forward
            </span>
          </div>
        </button>
      </div>

      {/* Instructions */}
      {assignment.instructions && (
        <div className="p-5">
          <h5 className="text-sm font-bold text-gray-900 dark:text-white mb-2">Instructions</h5>
          <p className="text-sm text-gray-500 dark:text-text-muted whitespace-pre-wrap">{assignment.instructions}</p>
        </div>
      )}

      {/* File Types */}
      {assignment.fileTypesAllowed.length > 0 && (
        <div className="px-5 pb-5">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 dark:text-text-muted">Allowed file types:</span>
            <div className="flex items-center gap-1.5">
              {assignment.fileTypesAllowed.map((type: string) => (
                <span
                  key={type}
                  className="px-2 py-0.5 rounded bg-gray-100 dark:bg-white/10 text-xs text-gray-600 dark:text-gray-300 uppercase"
                >
                  {type}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Late Submission Badge */}
      {assignment.allowLateSubmission && (
        <div className="px-5 pb-5">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500/10 text-blue-400 text-xs">
            <span className="material-symbols-outlined text-sm">schedule</span>
            Late submissions allowed
          </span>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <AssignmentFormModal
          assignment={assignment}
          lessonId={lessonId}
          onSave={assignment ? handleUpdate : handleCreate}
          onClose={() => setShowModal(false)}
          isSaving={assignment ? updateAssignment.isPending : createAssignment.isPending}
        />
      )}
    </div>
  )
}

export default AssignmentManager
